// Third Party
import chroma from 'chroma-js';
import moment from 'moment';

// Namespaces
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';

// DAOs
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';

// Utils
import colorUtils from '../../../../utils/colors';
import { formatLocalizedString, formatMessage } from '../../../../utils/i18n';

// Components
import { CorrelationMatrixDataCell, ProposalItemLabelCell, TextCell } from '../../../../views/components/cells';
import ProposalTableData from '@/types/proposal-table-data';
import ProposalTableRowData from '@/types/proposal-table-row-data';
import ProposalTableColGroup from '@/types/proposal-table-col-group';
import ProposalTableCellData from '@/types/proposal-table-cell-data';
import RiskPeriods from '@/types/risk-periods';
import CorrelationData from '@/types/correlation-data';

const DARK_TEXT_COLOR = '#5a6375';
const LIGHT_TEXT_COLOR = '#ffffff';
const colorScale = chroma.scale(['#c01b74', '#ece9f8', '#0a8adb']);

export default class ComparisonCorrelationMatrixDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new ComparisonCorrelationMatrixDAO(proposal, proposalElement);
  }

  data: ProposalTableData = { rows: [] };

  period: RiskPeriods = 'INC';

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const toolbarOption = this.getToolbarOption();
    let nbOfMonths: number = 36;
    if (toolbarOption !== undefined) {
      const start: Date = toolbarOption.startDate || new Date();
      nbOfMonths = moment(toolbarOption.endDate).diff(start, 'months');
    }

    switch (nbOfMonths) {
      case 0:
        this.period = 'INC';
        break;
      case 12:
        this.period = '1Y';
        break;
      case 36:
        this.period = '3Y';
        break;
      case 60:
        this.period = '5Y';
        break;
      case 120:
        this.period = '10Y';
        break;
      default:
        this.period = 'YT';
        break;
    }

    const items: ComparisonItemDAO[] = this.getItemDAOs();
    const { itemsCorrelation } = this.proposal;
    const correlation: DataPointResponse<CorrelationData[] | undefined> = DataPoints.getItemsCorrelationMatrixByPeriod(itemsCorrelation, this.period);
    const rows: ProposalTableRowData[] = [];
    const header: ProposalTableRowData = { data: [] };
    const colgroup: ProposalTableColGroup = {
      cols: [],
    };

    if (!correlation || !correlation.response || correlation.response.length === 0) {
      rows.push({
        data: [
          {
            value: formatMessage('Charts.Correlation.NoData'),
            cellType: TextCell,
          },
        ],
      });

      this.data = {
        rows,
      };

      return;
    }

    let itemDAO: ComparisonItemDAO | null = this.nextItem();
    while (itemDAO !== null) {
      this.addDisclaimerKeys(correlation.disclaimerKeys, itemDAO.item);
      itemDAO = this.nextItem();
    }

    header.data.push({
      value: this.formatMessage('Common.Items'),
      className: 'hidden',
    });

    colgroup.cols.push({
      className: 'correlation-y-col',
    });

    for (let i = 0; i < items.length; i++) {
      const baseItem = items[i];
      header.data.push({
        cellType: ProposalItemLabelCell,
        prefix: this.convertIndexToPrefix(i),
        value: '',
        color: baseItem.item.color,
        itemType: baseItem.item.type,
        clamp: 2,
        className: 'item-type',
      });
      const rowData = [];

      for (let j = 0; j < i + 1; j++) {
        const compareItem = items[j];
        if (baseItem.item._id === compareItem.item._id) {
          rowData.push({
            holdings: [baseItem.item._id, compareItem.item._id],
            value: null,
          });
        } else {
          const correlationPoint = correlation.response.find(
            (c: CorrelationData) => c.holdings.includes(baseItem.item._id) && c.holdings.includes(compareItem.item._id),
          );

          if (correlationPoint) {
            rowData.push(correlationPoint);
          } else {
            rowData.push({
              holdings: [baseItem.item._id, compareItem.item._id],
              value: null,
            });
          }
        }
      }

      const columns = this.getColumns(rowData, items);

      const labelCell = {
        color: baseItem.item.color,
        itemType: baseItem.item.type,
        prefix: this.convertIndexToPrefix(i),
        value: formatLocalizedString(baseItem.item.name),
        cellType: ProposalItemLabelCell,
        currency: this.proposal.showBaseCurrencies === true ? baseItem.item.currency : undefined,
        clamp: 1,
      };

      rows.push({
        data: [labelCell, ...columns],
      });
      colgroup.cols.push({});
    }

    this.data = {
      colgroup,
      header,
      rows,
    };
  }

  /* eslint-disable-next-line class-methods-use-this */
  getTableClassName(): string {
    return 'comparison-correlation-matrix-table';
  }

  getTableData(): ProposalTableData {
    return this.data;
  }

  private convertIndexToPrefix = (index: number): string => String.fromCharCode(index + 'A'.charCodeAt(0));

  private getColumns = (rowData: CorrelationData[], items: ComparisonItemDAO[]): ProposalTableCellData[] => {
    const column = rowData.map(datum => {
      if (datum.value != null) {
        const { value } = datum;

        // Force values into a [0,1] range to cover full scale.
        const backgroundColor = colorScale((value + 1) * 0.5).toString();
        const color = colorUtils.isLight(backgroundColor, 0.6) ? DARK_TEXT_COLOR : LIGHT_TEXT_COLOR;

        // Find holding symbols for data point
        const symbols = items.filter(h => datum.holdings.indexOf(h.item._id) > -1).map(item => formatLocalizedString(item.item.name));

        // Remove the integer part (or the first zero if value is 1 or -1): 0.12 -> .12 / 1.00 -> 1.0
        // IMPORTANT: Only use this function for values between -1 and 1, inclusively
        const shorten = (val: number) => val.toFixed(2).replace('0', '');

        const tooltipMessage = formatMessage('Charts.Correlation.ValueTooltip', {
          value: shorten(value),
          item1: symbols[0] || '',
          item2: symbols[1] || '',
        });

        return {
          className: 'value',
          value: shorten(value),
          cellType: CorrelationMatrixDataCell,
          color,
          backgroundColor,
          tooltipMessage,
        };
      }

      if (datum.holdings[0] === datum.holdings[1]) {
        return {
          value: '',
          className: 'identity-correlation',
          cellType: CorrelationMatrixDataCell,
        };
      }

      return {
        value: formatMessage('Common.NotApplicable'),
        cellType: CorrelationMatrixDataCell,
      };
    });

    return column
      .concat(
        Array(items.length).fill({
          value: '',
          cellType: CorrelationMatrixDataCell,
        }),
      )
      .slice(0, items.length);
  };
}
